import { RemoveCircle } from '@mui/icons-material';
import type { DataGridProProps as DataGridProps, GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRemoveFromRole } from '~/api/roles';
import { ActionButton } from '~/components/button';
import { SeparatedDataGrid } from '~/components/data-grid';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { EmptyState } from '~/components/empty-state';
import { AuthorizedLink } from '~/components/links';
import { ListCheckbox } from '~/components/list-row';
import { mergeGridActionProps, type GridActionCellProps } from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import AddDataSourcesIllustration from '~/images/illustrations/upload-data-source.svg';
import type {
  RoleDataSourceList__Role as Role,
  RoleDataSourceList__DataSourceRule as Rule,
} from '../RoleDataSourceList.generated';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={AddDataSourcesIllustration}
      description={t('pages.roles.data_sources.empty_state.description')}
      header={t('pages.roles.data_sources.empty_state.header')}
    />
  );
};

interface RemoveButtonProps extends GridActionCellProps {
  networkRole: Role;
  rule: Rule;
}

const RemoveButton = ({ networkRole, rule, ...cellProps }: RemoveButtonProps) => {
  const { t } = useTranslation();
  const [remove, { called, reset }] = useRemoveFromRole({
    variables: { roleId: networkRole.id, dataSourceRuleIds: rule.id },
  });

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const onClick = async () => {
    if (!(await confirmDelete())) return;
    await remove();
    reset();
  };

  return (
    <>
      <ActionButton
        color="error"
        disabled={called}
        Icon={RemoveCircle}
        {...mergeGridActionProps({ onClick }, cellProps)}
        title={t('common.actions.remove')}
      />

      <ConfirmDialog
        {...confirmDeleteProps}
        cancel={t('common.buttons.cancel')}
        confirm={t('pages.roles.dialogs.remove_data_source.confirm')}
        deleteConfirm
        prompt={t('pages.roles.dialogs.remove_data_source.prompt', {
          data_source_name: rule.dataSource.name,
          role_name: networkRole.name,
        })}
        title={t('pages.roles.dialogs.remove_data_source.title')}
      />
    </>
  );
};

const useColumns = (role: Role): GridColDef<Rule>[] => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        field: 'name',
        headerName: t('common.labels.name'),
        flex: 1,
        valueGetter: (_value, row) => row.dataSource.name,
        renderCell: ({ row }) => (
          <AuthorizedLink authorized={row.dataSource.canUpdate.value} entity={row.dataSource} />
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        getActions: ({ row }) => [<RemoveButton key={0} networkRole={role} rule={row} />],
      },
    ],
    [role, t],
  );
};

export interface DataSourceTableProps extends Omit<DataGridProps<Rule>, 'columns'> {
  loading: boolean;
  role: Role;
}

export const DataSourceTable = ({ loading, role, ...props }: DataSourceTableProps) => {
  const columns = useColumns(role);

  return (
    <SeparatedDataGrid
      columns={columns}
      loading={loading}
      slots={{
        baseCheckbox: ListCheckbox,
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      {...props}
    />
  );
};
